import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';

import Logo from 'components/Header/LogoSidebar';

import { callApi } from 'util/CallAPI';
import { IntlMessage } from 'util/IntlMessages';
import { useStorage } from 'util/hooks';
import { classNames, isEmpty, isString } from 'util/utils';

import { commonLogout, IS_QUICK_SEARCH, TRACK_TYPE_MAP } from 'constants/Common';
import { GET_DASHBOARD_DATA, GET_EXCLUSIVE_COLLECTION } from 'constants/apiConstant';

import { ReactComponent as LogoutIcon } from 'assets/svg/Header/logout.svg';
import { ReactComponent as DashboardIcon } from 'assets/svg/sidebar/Dashboard.svg';
import { ReactComponent as CoupleDiamondIcon } from 'assets/svg/sidebar/couple-diamond.svg';
import { ReactComponent as DiamondIcon } from 'assets/svg/sidebar/diamond.svg';
import { ReactComponent as MyCartIcon } from 'assets/svg/sidebar/my-cart.svg';
import { ReactComponent as MyOfferIcon } from 'assets/svg/sidebar/my-offer.svg';
import { ReactComponent as NewArrivalsIcon } from 'assets/svg/sidebar/new-arrivals.svg';
import { ReactComponent as SearchIcon } from 'assets/svg/sidebar/search.svg';
import { ReactComponent as SpecialStoneIcon } from 'assets/svg/sidebar/special-stone.svg';
import { ReactComponent as UpcomingIcon } from 'assets/svg/sidebar/upcoming.svg';
import { ReactComponent as WatchlistIcon } from 'assets/svg/sidebar/watchlist.svg';

import './sidebarHeader.less';

function SidebarHeader() {
  const location = useLocation();

  const [tracks, setTracks] = React.useState({});

  const [P] = useStorage('userPermissions');

  const getDashboards = React.useCallback(async () => {
    const [, res] = await callApi({ ...GET_DASHBOARD_DATA, request: { track: true, savedSearch: true } });
    if (res?.code === 'OK') {
      setTracks(res.data.tracks);
    }
  }, []);

  React.useEffect(() => {
    getDashboards();
  }, [getDashboards, location?.pathname]);

  const [exclusiveList, setExclusiveList] = React.useState([]);

  const fetchExclusiveCollection = React.useCallback(async () => {
    const [, res] = await callApi(GET_EXCLUSIVE_COLLECTION);
    if (res?.code === 'OK' && res?.data) {
      const list = res.data.list;
      setExclusiveList(list);
    }
  }, []);

  React.useEffect(() => {
    fetchExclusiveCollection();
  }, [fetchExclusiveCollection, location?.pathname]);

  return (
    <div className="sidebarHeader sidebarFixed">
      <NavLink to="/dashboard" className="sidebarLogo">
        <Logo />
      </NavLink>
      <ScrollArea
        speed={0.8}
        className="settingLeftScroll area sideBarScroll"
        contentClassName="content"
        horizontal={false}
        smoothScrolling
      >
        <div className="sidebarMenuList">
          <ul>
            {P?.DASHBOARD?.view && (
              <li className={`${location?.pathname === `/dashboard` && `active`}`}>
                <NavLink to="/dashboard">
                  <DashboardIcon />
                  <span>
                    <IntlMessage id="app.dashboard" />
                  </span>
                </NavLink>
              </li>
            )}
            {P?.SEARCH_DIAMOND?.view && (
              <li className={`${location?.pathname === `/diamond-search` && `active`}`}>
                <NavLink to="/diamond-search">
                  <SearchIcon />
                  <span>
                    <IntlMessage id="app.Search" />
                  </span>
                </NavLink>
              </li>
            )}
            {P?.QUICK_SEARCH?.view && IS_QUICK_SEARCH && (
              <li className={`${location?.pathname === `/quick-search` && `active`}`}>
                <NavLink to="/quick-search">
                  <SearchIcon />
                  <span>
                    <IntlMessage id="app.quick-search" />
                  </span>
                </NavLink>
              </li>
            )}
            {P?.SEARCH_MATCH_PAIR?.view && (
              <li className={`${location?.pathname === `/search-match-pair` && `active`}`}>
                <NavLink to="/search-match-pair">
                  <CoupleDiamondIcon />
                  <span>
                    <IntlMessage id="app.couple-stone" />
                  </span>
                </NavLink>
              </li>
            )}
            {/* {P?.BID?.view && (
              <li className={`${location?.pathname === `/new-arrival-bid-it` && `active`}`}>
                <NavLink to="/new-arrival-bid-it">
                  <div className={!isEmpty(bidDiamond) ? 'ribbon ribbon-top' : 'ribbon ribbon-top-right'}>
                    <span>{!isEmpty(bidDiamond) ? 'Active' : 'Inactive'}</span>
                  </div>
                  <NewArrivalsIcon />
                  <span>Bid</span>
                </NavLink>
              </li>
            )} */}
            {/* {permissions?.EXCLUSIVE?.view && (
              <li className={`${pathname === `/special-stone` && `active`}`}>
                <Link to="/special-stone">
                  <SpecialStoneIcon />
                  <span>Special Stone</span>
                </Link>
              </li>
            )} */}
            {P?.COLLECTION?.view &&
              exclusiveList.map((search) => (
                <li
                  key={search.id}
                  className={classNames([
                    `/exclusive${window.location.search}` === `/exclusive?${search.id}` && `active`,
                  ])}
                >
                  <NavLink to={`/exclusive?${search.id}`}>
                    <SpecialStoneIcon />
                    <span>{isString(search?.name) ? search.name : 'Exclusive Stone'}</span>
                  </NavLink>
                </li>
              ))}
            {P?.STONE_OF_THE_DAY?.view && (
              <li className={`${location?.pathname === `/stone-of-the-day` && `active`}`}>
                <NavLink to="/stone-of-the-day">
                  <DiamondIcon />
                  <span>
                    <IntlMessage id="app.feature-stone" />
                  </span>
                </NavLink>
              </li>
            )}
            {P?.UPCOMING?.view && (
              <li className={`${location?.pathname === `/upcoming` && `active`}`}>
                <NavLink to="/upcoming">
                  <UpcomingIcon />
                  <span>
                    <IntlMessage id="app.Upcoming" />
                  </span>
                </NavLink>
              </li>
            )}
            {P?.CART?.view && (
              <li className={`${location?.pathname === `/account/cart` && `active`}`}>
                <NavLink to="/account/cart">
                  <MyCartIcon />
                  <span>
                    <IntlMessage id="app.my-cart" />
                  </span>
                  {Boolean(tracks?.[TRACK_TYPE_MAP.CART]?.pieces) && (
                    <div className="_count">
                      <span>{tracks?.[TRACK_TYPE_MAP.CART]?.pieces}</span>
                    </div>
                  )}
                </NavLink>
              </li>
            )}
            {P?.WATCHLIST?.view && (
              <li className={`${location?.pathname === `/account/watchlist` && `active`}`}>
                <NavLink to="/account/watchlist">
                  <WatchlistIcon />
                  <span>
                    <IntlMessage id="app.my-watchlist" />
                  </span>
                  {Boolean(tracks?.[TRACK_TYPE_MAP.WATCHLIST]?.pieces) && (
                    <div className="_count">
                      <span>{tracks?.[TRACK_TYPE_MAP.WATCHLIST]?.pieces}</span>
                    </div>
                  )}
                </NavLink>
              </li>
            )}
            {P?.QUOTE?.view && (
              <li className={`${location?.pathname === `/account/quote-list` && `active`}`}>
                <NavLink to="/account/quote-list">
                  <MyOfferIcon />
                  <span>
                    <IntlMessage id="app.offerDashboard" />
                  </span>
                  {Boolean(tracks?.[TRACK_TYPE_MAP.QUOTE]?.pieces) && (
                    <div className="_count">
                      <span>{tracks?.[TRACK_TYPE_MAP.QUOTE]?.pieces}</span>
                    </div>
                  )}
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </ScrollArea>
      <div className="sidebarMenuList sidebarListBottom">
        <ul>
          <li onClick={commonLogout}>
            <a href="#!">
              <div>
                <LogoutIcon />
              </div>
              <span>
                <IntlMessage id="app.Logout" />
              </span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default React.memo(SidebarHeader);
